import { LANGUAGES } from 'state/intl/types';

// Matches a JSON string literal's contents, handling escaped characters.
const JSON_STRING_VALUE = String.raw`((?:\\.|[^"\\])*)`;

// A plain `{"en":...,"fr":...}` pair (as used by e.g. `ingredients.name`/`components`), in
// either source order. Most localized-text fields (item/offer name, description, image altText)
// are NOT shaped like this — the gateway query (src/queries/gateway/menu.graphql) aliases their
// English field to `locale` at query time, so they arrive as `{"locale":...,"fr":...}` and never
// match these patterns.
const EN_THEN_FR = new RegExp(`"en":"${JSON_STRING_VALUE}","fr":"${JSON_STRING_VALUE}"`, 'g');
const FR_THEN_EN = new RegExp(`"fr":"${JSON_STRING_VALUE}","en":"${JSON_STRING_VALUE}"`, 'g');
// Untranslated CDN fields arrive as JSON null. Resolve them to the English string
// before the generic key rewrite so a null `fr` cannot replace that English value.
const EN_THEN_FR_NULL = new RegExp(`"en":"${JSON_STRING_VALUE}","fr":null`, 'g');
const FR_NULL_THEN_EN = new RegExp(`"fr":null,"en":"${JSON_STRING_VALUE}"`, 'g');
const LOCALE_THEN_FR_NULL = new RegExp(`"locale":"${JSON_STRING_VALUE}","fr":null`, 'g');
const FR_NULL_THEN_LOCALE = new RegExp(`"fr":null,"locale":"${JSON_STRING_VALUE}"`, 'g');

/**
 * Rewrites a raw OneMenu JSON payload's localized-text keys to a single `"locale":"..."` key
 * per field, before parsing — avoids updating every `.locale` read in the app to be dynamic per
 * language.
 *
 * For plain `{"en":...,"fr":...}` fields, a blind single-key replace is order-dependent —
 * `JSON.parse` keeps the positionally-last duplicate `locale` key, not whichever replace ran
 * logically last — so it can pick the wrong language if French happens to appear before English
 * in the source. With French active, those pairs are resolved to their winning value explicitly
 * first, independent of source order.
 */
export const applyLocaleTextTransform = (text: string, activeLanguage: LANGUAGES): string => {
  if (activeLanguage !== LANGUAGES.fr) {
    return text.replaceAll(`"${LANGUAGES.en}":"`, '"locale":"');
  }

  const withNullFrenchResolved = text
    .replace(EN_THEN_FR_NULL, (_match, en) => `"locale":"${en}"`)
    .replace(FR_NULL_THEN_EN, (_match, en) => `"locale":"${en}"`)
    .replace(LOCALE_THEN_FR_NULL, (_match, locale) => `"locale":"${locale}"`)
    .replace(FR_NULL_THEN_LOCALE, (_match, locale) => `"locale":"${locale}"`);

  const withPairsResolved = withNullFrenchResolved
    .replace(EN_THEN_FR, (_match, en, fr) => `"locale":"${fr}"`)
    .replace(FR_THEN_EN, (_match, fr, en) => `"locale":"${fr}"`);

  return withPairsResolved
    .replaceAll(`"${LANGUAGES.fr}":"`, '"locale":"')
    .replaceAll(`"${LANGUAGES.en}":"`, '"locale":"');
};
